import { useState } from "react";
import IntakeFormMultipleOptions from "./IntakeFormMultipleOptions.jsx";
import {
  OTHER_OPTION_VALUE,
  OTHER_OPTION_LABEL,
  isOtherOption,
  concreteOptions,
} from "../../../helpers/multipleOptions/otherOption";

/**
 * De keuzelijst van een 'kies er één'-vraag op het publieke intakeformulier,
 * met de bijbehorende 'Anders, namelijk...'-tekst.
 *
 * De opties komen als prop binnen, niet uit Firestore: deze pagina heeft geen
 * login en haalt haar hele definitie via de Cloud Function op.
 * AI-CONTEXT: See docs/decisions.md#adr-022
 *
 * Wat er bewaard wordt is altijd het ANTWOORD, nooit de markering: kiest de
 * invuller 'Anders, namelijk...', dan is de waarde zijn eigen tekst. Anders
 * staat er straks letterlijk 'other' in de gegevens van het huishouden.
 *
 * @param {object} props
 * @param {Array<{ID: string, Option: string}>} [props.options] - Antwoordopties, al op volgorde
 * @param {string} props.value - Het huidige antwoord
 * @param {(value: string) => void} props.onChange - Krijgt het nieuwe antwoord
 * @param {boolean} [props.required] - Of de vraag verplicht is
 * @param {boolean} [props.invalid] - Of de vraag als fout gemarkeerd moet worden
 * @param {string} [props.label] - De vraagtekst; wordt de naam van de keuzelijst voor een schermlezer
 */
const IntakeFormSingleChoice = ({ options, value, onChange, required, invalid, label }) => {
  const activeOptions = options || [];
  const vasteOpties = concreteOptions(activeOptions);
  const heeftAnders = activeOptions.some(isOtherOption);

  const vasteWaarden = vasteOpties.map((o) => o.Option);

  // Een vrije tekst matcht geen enkele vaste optie, dus zo is 'anders' terug te
  // vinden zonder hem apart te bewaren. Alleen 'net gekozen, nog niets getypt'
  // valt daarbuiten — daar is de state voor.
  const heeftVrijAntwoord = heeftAnders && value !== "" && !vasteWaarden.includes(value);

  const [andersGekozen, setAndersGekozen] = useState(false);
  const toonAndersVeld = heeftAnders && (andersGekozen || heeftVrijAntwoord);

  const kiesHandler = (gekozen) => {
    if (gekozen === OTHER_OPTION_VALUE) {
      setAndersGekozen(true);
      // Leeg, niet de markering: een verplichte vraag hoort nog niet ingevuld te
      // zijn zolang de invuller zijn eigen antwoord niet getypt heeft.
      onChange("");
      return;
    }

    setAndersGekozen(false);
    onChange(gekozen);
  };

  return (
    <>
      <select
        aria-label={label}
        value={toonAndersVeld ? OTHER_OPTION_VALUE : value}
        required={required}
        aria-invalid={invalid}
        onChange={(e) => kiesHandler(e.target.value)}
      >
        <option value="">Selecteer een optie</option>
        {/* De vaste keuzes via de bestaande renderer, zodat er niet twee plekken
            zijn die een optielijst tekenen. */}
        <IntakeFormMultipleOptions options={vasteOpties} />
        {heeftAnders && (
          <option value={OTHER_OPTION_VALUE}>{OTHER_OPTION_LABEL}</option>
        )}
      </select>

      {toonAndersVeld && (
        <input
          type="text"
          className="intake-other-input"
          // Een eigen naam, niet die van de keuzelijst ernaast: twee
          // bedieningselementen met dezelfde naam zijn voor een schermlezer niet
          // uit elkaar te houden.
          aria-label={`Jouw antwoord bij ${OTHER_OPTION_LABEL}`}
          value={heeftVrijAntwoord ? value : ""}
          placeholder="Vul hier je antwoord in"
          aria-invalid={invalid}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </>
  );
};

export default IntakeFormSingleChoice;
